// /src/api/http.js
import { getToken, logout } from "../utils/auth.js";

/**
 * Parcel replaces process.env.VAR at build time.
 * DO NOT use optional chaining or dynamic access with process.env.
 */
const IS_BROWSER = typeof window !== "undefined";

// These are replaced to string literals by Parcel at build time
const NODE_ENV = process.env.NODE_ENV; // "development" or "production"
const ENV_BASE = process.env.BASE_URL; // from .env.development / .env.production

/**
 * Priority for API base:
 * 1) window.__API_BASE_URL__  (runtime override from HTML, optional)
 * 2) process.env.BASE_URL     (injected by Parcel from .env.*)
 * 3) Fallback by NODE_ENV
 */
const FALLBACK_BASE = NODE_ENV === "production" ? "http://localhost:3000/api/v1" : "http://localhost:3000/api/v1";

const RAW_BASE = (IS_BROWSER && window.__API_BASE_URL__) || ENV_BASE || FALLBACK_BASE;

// Normalize base so origin and path are easy to handle
const BASE = new URL(RAW_BASE, IS_BROWSER ? window.location.origin : "http://localhost");
const BASE_ORIGIN = BASE.origin; // e.g. http://localhost:3000
const BASE_PATH = BASE.pathname.replace(/\/+$/, ""); // e.g. /api/v1 (no trailing /)

// Helpful log during dev serves
if (NODE_ENV === "development") {
	console.log("[http] API BASE =", BASE.href);
}

function resolveUrl(inputPath) {
	// Absolute URL? use as-is
	if (/^https?:\/\//i.test(inputPath)) return inputPath;

	// Ensure leading slash
	const p = inputPath.startsWith("/") ? inputPath : `/${inputPath}`;

	// If caller already included the base path (/api/v1), don't duplicate it
	if (BASE_PATH && (p.startsWith(BASE_PATH + "/") || p === BASE_PATH)) {
		return BASE_ORIGIN + p;
	}

	// Otherwise, prefix with base path
	return BASE_ORIGIN + BASE_PATH + p;
}

function isFormData(body) {
	return typeof FormData !== "undefined" && body instanceof FormData;
}

async function request(method, url, data = null, customHeaders = {}) {
	const token = getToken();
	const form = isFormData(data);

	// Only set JSON header for non-FormData requests
	const headers = { ...customHeaders };
	if (!form) headers["Content-Type"] = headers["Content-Type"] || "application/json";
	if (token) headers["Authorization"] = `Bearer ${token}`;

	const config = { method, headers };

	if (method !== "GET" && method !== "HEAD") {
		config.body = form ? data : JSON.stringify(data ?? {});
	}

	const finalUrl = resolveUrl(url);

	try {
		const res = await fetch(finalUrl, config);
		const contentType = res.headers.get("content-type") || "";
		const isJson = contentType.includes("application/json");
		const body = isJson ? await res.json() : await res.text();

		if (!res.ok) {
			const isAuthFailure = res.status === 401;
			const isLoginAttempt = typeof url === "string" && url.includes("/auth/login");

			if (isAuthFailure && !isLoginAttempt) {
				setTimeout(() => logout(), 1000);
			}

			throw {
				status: res.status,
				message: (isJson ? body?.message : null) || "Request failed",
				data: body,
			};
		}

		return body;
	} catch (err) {
		throw {
			status: err.status || 500,
			message: err.message || "Network error",
			data: err.data || null,
		};
	}
}

export const http = {
	get: (url, headers) => request("GET", url, null, headers),
	post: (url, data, headers) => request("POST", url, data, headers),
	put: (url, data, headers) => request("PUT", url, data, headers),
	patch: (url, data, headers) => request("PATCH", url, data, headers),
	del: (url, headers) => request("DELETE", url, null, headers),
};

export const API_BASE = BASE.href;
